import { useEffect, useMemo, useState } from 'react';

import * as T from '../../../../components/Typography';
import TryAgainModal from '../../../../components/ModalNew/TryAgainModal';
import { PlayButton } from '../../../../components/Button';
import { useLocation } from 'react-router-dom';
import StepFooterButtons from '../StepFooterButtons';

import * as S from './style';

const getInitialAnswers = (options) => {
  const _answers = {};
  options?.forEach((option) => {
    _answers[option.id] = option.showValue ? option.option : '';
  });

  return _answers;
};

const Pyramid = ({
  data: { textAudio, textAudioUrl, options, rowSize } = {},
  handleNext,
  stuckAtReview,
  disabled,
  preview,
}) => {
  const [showTryAgain, setShowTryAgain] = useState(false);
  const [answers, setAnswers] = useState(() => getInitialAnswers(options));

  const location = useLocation();
  useEffect(() => {
    if (options?.length) {
      setAnswers(getInitialAnswers(options));
    }

    // To avoid unnecessary re-renders we only want this to work when the pathname changes,
    // if react-query still refetching step is handled in the parent (isStepLoading)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const handleClick = () => {
    const allCorrect = options?.every(
      (option) => answers[option.id] === option.option
    );
    if (allCorrect) {
      handleNext();
    } else {
      setShowTryAgain(true);
    }
  };

  const showSquares = rowSize >= 3 && rowSize <= 4;
  const rows = useMemo(() => {
    if (!showSquares) {
      return [];
    }
    const _options = options || [];
    const result = [];
    let index = 0;
    let rowLength = 1;

    while (index < _options.length) {
      result.push(_options.slice(index, index + rowLength));
      index += rowLength;
      rowLength += 1;
    }

    return result;
  }, [options, showSquares]);

  const isAllInputsFilled = Object.values(answers).every((value) => !!value);

  return (
    <S.Wrapper>
      <S.ContentWrapper>
        {!!textAudio && (
          <S.TextAudioWrapper mt="2" mb="4">
            {textAudioUrl ? (
              <>
                <PlayButton
                  audioUrl={textAudioUrl}
                  width="56px"
                  height="56px"
                  iconProps={{ width: '32px', height: '32px' }}
                  fallbackText={textAudio}
                />
                <T.P ml={4} weight="regular" size="regular">
                  {textAudio}
                </T.P>
              </>
            ) : (
              <T.P weight="regular" size="regular">
                {textAudio}
              </T.P>
            )}
          </S.TextAudioWrapper>
        )}

        {!!rows?.length && (
          <S.PreFilledAnswersGridWrapper>
            {rows.map((rowOptions, rowIndex) => (
              <S.PyramidRowWrapper key={rowIndex}>
                {rowOptions.map((option) => {
                  const id = option.id;
                  const value = answers[id];
                  const isCorrect = value === option.option;
                  return (
                    <S.TypeInput
                      key={id}
                      id={`answer-${id}`}
                      name={`answer-${id}`}
                      type="text"
                      aria-labelledby={`answer-${id}`}
                      rowSize={rowSize}
                      showValue={option.showValue}
                      value={value}
                      disabled={option.showValue}
                      onChange={(e) => {
                        setAnswers({
                          ...answers,
                          [option.id]: e.target.value,
                        });
                      }}
                      isCorrect={isCorrect}
                    />
                  );
                })}
              </S.PyramidRowWrapper>
            ))}
          </S.PreFilledAnswersGridWrapper>
        )}
      </S.ContentWrapper>

      <StepFooterButtons
        stuckAtReview={stuckAtReview}
        handleNext={handleClick}
        preview={preview}
        disabled={disabled}
        enableNext={isAllInputsFilled}
      />
      <TryAgainModal
        aria-modal="true"
        isModalVisible={showTryAgain}
        setIsModalVisible={setShowTryAgain}
      />
    </S.Wrapper>
  );
};

export default Pyramid;
